import React, {useCallback, useEffect, useState} from "react";
import {Combination, MenuItem, MenuItemCombination} from "../../@types/stores/menu";
import { observer } from "mobx-react";
import "./style.scss";
import {getCombinationArticleOptionsUrl, getHost, getNextCombinationStep, getNextPackageArticleOptionsUrl} from "../../helpers/ApiHelper";
import defaultImg from "../../assets/images/default.webp";
import { OrderLineIngredient, OrderLineIngredients } from "../../@types/stores/order";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router";
import IngredientsGroup from "../MenuItem/IngredientsGroup";
import lodash from "lodash";
import orderStore from "../../stores/order";
import { getIngredientsForOrderLine } from "../../helpers/orderHelper";
import { moveToTop } from "../../helpers/DomHelper";
import menuStore from "../../stores/menu";
import useLayoutStyles from "../../hooks/styles";


export interface CombinationArticleIngredientsProps {
    combination: MenuItemCombination;
    step: Combination;
    item: MenuItem;
    menuId: number;
    subMenuId: number;
}

const CombinationArticleIngredients = ({item, combination, step, menuId, subMenuId}: CombinationArticleIngredientsProps) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const styles = useLayoutStyles();

    const [ingredients, setIngredients] = useState<OrderLineIngredients[]>([]);
    const [allowNext, setAllowNext] = useState<boolean>(true);

    useEffect(() => {
        if (item.ingredientGroups) {
            const defIngredients = getDefaultSelectedItems();
            const _ingredients = orderStore.getCurrentCombinationArticleOrderLine(step, item)?.ingredients;
            if (_ingredients?.length && !ingredients.length) {
                for (const di of defIngredients) {
                    const i = _ingredients.find(x => x.groupId === di.groupId);
                    if (i) {
                        di.items.forEach(x => {
                            if (!i.items.some(y => y.id === x.id))
                                i.items.push(x);
                        });
                    }
                }
                setIngredients(_ingredients);
            }
            else
                setIngredients(defIngredients);
        }
        else
            goNext();
    }, [location]);

    useEffect(() => {
        // if recommendation article
        if (location.pathname.indexOf("/recommendations/") === 0 && typeof params.groupId !== "undefined") {
            menuStore.changeRecommendationGroup(+params.groupId);
        }
    }, [params]);

    useEffect(() => {
        checkAllowNext();
        reCalcOrderLine();
    }, [ingredients]);

    const getDefaultSelectedItems = (): OrderLineIngredients[]  => {
        const _selected: OrderLineIngredients[] = [];

        const defaultSelectedGropus = item.ingredientGroups?.filter(ig => ig.items.some(i => !!i.default));
        if (defaultSelectedGropus) {
            defaultSelectedGropus.forEach(ig => {
                _selected.push({
                    groupId: ig.id,
                    items: ig.items.filter(i => !!i.default).map(i => {
                        return {
                            id: i.id,
                            number: i.number,
                            quantity: i.quantity,
                            price: i.unitPrice,
                            sellUnit: i.sellUnit,
                            default: i.default || false
                        };
                    })
                })
            });
        }
        return _selected;
    }

    const checkAllowNext = () => {
        let allow = true;

        if (item.ingredientGroups) {
            for (const group of item.ingredientGroups) {
                const selectedCount = ingredients.find(x => x.groupId === group.id)?.items.length || 0;
                const min = group.min || 0;
                if (selectedCount < min)
                    allow = false;
            }
        }

        setAllowNext(allow);
    }

    const changeIngredients = (id: string, selected: OrderLineIngredient[]) => {
        const group = item.ingredientGroups?.find(x => x.id === id);
        if (group) {
            const _ingridients = lodash.cloneDeep(ingredients).filter(x => x.groupId !== id);

            let allowAdd = true;
            if (group.max && selected.length > group.max) {
                allowAdd = false;
            }

            if (allowAdd) {
                _ingridients.push({
                    groupId: id,
                    items: selected
                });
                setIngredients(_ingridients);
            }
        }
    }

    const reCalcOrderLine = () => {
        if (ingredients.length) {
            const orderLine = orderStore.getCurrentCombinationArticleOrderLine(step, item);
            if (orderLine) {
                orderLine.ingredients = getIngredientsForOrderLine(ingredients, item.ingredientGroups);
                orderStore.updateCurrentCombinationArticleOrderLine(combination, step, orderLine);
            }
        }
    }

    const goNext = () => {
        if (!allowNext) return;
        const isChange = params.change === "change";

        moveToTop();

        if (combination.isPackage) {
            const nextArticleUrl = getCombinationArticleOptionsUrl(menuId, subMenuId, combination, step, item, isChange, "ingredients");
            navigate(nextArticleUrl || (isChange
                ? `/menu/combination/info`
                : getNextPackageArticleOptionsUrl(menuId, subMenuId, combination, step, item)));
            return;
        }

        const nextStep = getNextCombinationStep(combination, step);
        if (!isChange && nextStep)
            navigate(`/menu/combination/${menuId}/${subMenuId}/${combination.id}/step/${nextStep.id}`);
        else
            navigate(`/menu/combination/info`);
    }

    const getSelectedIngredients = useCallback((id: string) => {
        return ingredients.find(x => x.groupId === id)?.items || [];
    }, [ingredients]);

    if (!item)
        return null;

    return <div className="Ingredients">
        <h2>{item.name}</h2>
        <div className="img" style={{backgroundImage: `url(${item.previewEdit ? `${getHost()}${item.previewEdit}` : (styles?.article.defaultEditImage ? `${getHost()}${styles.article.defaultEditImage}` : defaultImg)}`}} />
        <div className="description">{item.description || ""}</div>
        <div className="ingredients">
            {item.ingredientGroups?.length
                ? item.ingredientGroups.map(ig => <IngredientsGroup key={`ingredientsGroup_${ig.id}`} ingredientsGroup={ig} onChange={changeIngredients} selected={getSelectedIngredients(ig.id)} />)
                : null
            }
        </div>
        <div className={`button dark`} onClick={goNext}>{t(`next`)}</div>
    </div>
}

export default observer(CombinationArticleIngredients);
